import * as React from "react";
import { Button, Gapped, Input, Modal, ComboBox } from "react-ui";

import { search, verify } from "./KladrAPI";
import { Address, isPlaceDescription, Place, PlaceDescription, VerifyResult } from "./Types";
import * as util from "./util";
import cn from "./AddressModal.less";
import { Nullable } from "./Nullable";

export interface AddressModalProps {
    address: Address;
    title: string;
    baseUrl: string;
    onChange: (value: { address: Address }) => void;
    onClose: () => void;
}

export interface AddressModalState {
    address: Address;
    invalidField: Nullable<string>;
}

interface Info {
    searchText: string;
    address: Address;
}

interface ItemResult {
    place: PlaceDescription | string | null | undefined;
    info?: Info;
}

type GetItemsFunction = (searchText: string) => Promise<ItemResult[]>;
type ValueToStringFunction = (value: ItemResult) => string;
type RenderItemFunction = (value: ItemResult) => JSX.Element;
type ValueChangeHandler = (value: ItemResult) => void;
type UnexpectedInputHandler = (value: string) => void;
type ItemToValueFunction = (value: ItemResult) => string | number;

interface FieldProps {
    getItems: GetItemsFunction;
    onValueChange: ValueChangeHandler;
    onUnexpectedInput: UnexpectedInputHandler;
    renderItem: RenderItemFunction;
    renderValue: ValueToStringFunction;
    valueToString: ValueToStringFunction;
    itemToValue: ItemToValueFunction;
}

interface SimpleFieldProps {
    onValueChange: (value: string) => void;
}

const PLACES: Record<string, Place> = {
    "0": "index",
    "1": "region",
    "2": "district",
    "4": "city",
    "8": "settlement",
    "16": "street",
};

export class AddressModal extends React.Component<AddressModalProps, AddressModalState> {
    private readonly _regionProps: FieldProps;
    private readonly _districtProps: FieldProps;
    private readonly _cityProps: FieldProps;
    private readonly _settlementProps: FieldProps;
    private readonly _streetProps: FieldProps;
    private readonly _houseProps: SimpleFieldProps;
    private readonly _buildingProps: SimpleFieldProps;
    private readonly _roomProps: SimpleFieldProps;
    private _verifyPromise?: null | Promise<VerifyResult>;

    public constructor(props: AddressModalProps) {
        super(props);

        this.state = {
            address: props.address || {},
            invalidField: null,
        };

        this._regionProps = this.createFieldProps("region", [], "Region");
        this._districtProps = this.createFieldProps("district", ["region"], "District");
        this._cityProps = this.createFieldProps("city", ["region", "district"], "City");
        this._settlementProps = this.createFieldProps("settlement", ["region", "district", "city"], "Settlement");
        this._streetProps = this.createFieldProps("street", ["region", "district", "city", "settlement"], "Street");
        this._houseProps = this.createSimpleFieldProps("house", true);
        this._buildingProps = this.createSimpleFieldProps("building", true);
        this._roomProps = this.createSimpleFieldProps("room", false);
    }

    private createFieldProps(field: Place, parents: Place[], level: string): FieldProps {
        return {
            getItems: this.createGetItems(field, parents, level),
            onValueChange: this.createHandler(field),
            onUnexpectedInput: this.createUnexpectedInputHandler(field),
            renderItem: this.renderItem.bind(this, field, parents),
            renderValue: this.renderValue.bind(this, field),
            valueToString: this.renderValue.bind(this, field),
            itemToValue: this.itemToValue.bind(this),
        };
    }

    private createSimpleFieldProps(
        field: "house" | "room" | "building",
        enableCheckOnChange: boolean
    ): SimpleFieldProps {
        return {
            onValueChange: async value => {
                const address: Address = {
                    ...this.state.address,
                    [field]: value,
                };
                this.setState({ address: address });
                if (enableCheckOnChange) {
                    await this.check(address);
                }
            },
        };
    }

    private createHandler(place: Place): ValueChangeHandler {
        return async (_value: ItemResult | Promise<ItemResult>) => {
            const value = await _value;
            const address = structuredClone(this.state.address);
            if (value.info) {
                for (const itemPlace of Object.values(PLACES)) {
                    if (itemPlace !== place && value.info.address[itemPlace]) {
                        // @ts-ignore
                        address[itemPlace] = value.info.address[itemPlace];
                    } else if (itemPlace === place) {
                        // @ts-ignore
                        address[place] = value.place;
                        break;
                    }
                }
            } else if (isPlaceDescription(value.place) && value.place.name) {
                // @ts-ignore
                address[place] = value.place;
            }

            this.setState({ address: address });
            await this.check(address);
        };
    }

    private createUnexpectedInputHandler(place: Place): UnexpectedInputHandler {
        return async (value: string) => {
            const address = structuredClone(this.state.address);
            // @ts-ignore
            address[place] = {
                name: value,
            };

            this.setState({ address: address });
            await this.check(address);

            return { place: address };
        };
    }

    private async check(address: Address) {
        const promise = verify(address, this.props.baseUrl);
        this._verifyPromise = promise;

        await promise.then(result => {
            if (promise !== this._verifyPromise) {
                return;
            }
            this._verifyPromise = null;

            let invalidField = null;
            if (!result.isKladrAddress) {
                invalidField = PLACES[result.invalidItem];
            }
            this.setState({
                address: {
                    ...this.state.address,
                    ...result.address,
                },
                invalidField: invalidField,
            });
        });
    }

    private createGetItems(field: Place, parents: Place[], level: string): GetItemsFunction {
        return (searchText: string) => {
            let parentCode = null;
            for (const parentName of parents) {
                const parent = this.state.address[parentName];
                if (isPlaceDescription(parent) && parent.code) {
                    parentCode = parent.code;
                }
            }

            return search(searchText, `[${level}]`, parentCode, this.props.baseUrl).then(values =>
                values.map(address => ({
                    place: address[field],
                    info: { searchText: searchText, address: address },
                }))
            );
        };
    }

    private setStateAddress(key: string, value: PlaceDescription | string) {
        this.setState({
            address: {
                ...this.state.address,
                [key]: value,
            },
        });
    }

    private itemToValue(item: ItemResult): string | number {
        // eslint-disable-next-line @typescript-eslint/no-base-to-string
        return isPlaceDescription(item.place) ? item.place.code : String(item);
    }

    private handleSave() {
        this.props.onChange({ address: this.state.address });
        this.props.onClose();
    }

    public render() {
        return (
            <Modal width={520} onClose={this.props.onClose}>
                <Modal.Header>{this.props.title}</Modal.Header>
                <Modal.Body>{this.renderForm()}</Modal.Body>
                <Modal.Footer panel>
                    <Gapped>
                        <Button size="medium" use="primary" onClick={this.handleSave.bind(this)}>
                            Сохранить
                        </Button>
                        <Button size="medium" onClick={this.props.onClose}>
                            Отмена
                        </Button>
                    </Gapped>
                </Modal.Footer>
            </Modal>
        );
    }

    private renderForm() {
        const { invalidField } = this.state;

        return (
            <Gapped vertical>
                <div className={cn("row")}>
                    <div className={cn("label")}>Индекс</div>
                    <div className={cn("field")}>
                        <Input
                            value={this.state.address.index ?? ""}
                            width="100%"
                            data-tid={"KladrSimpleInput"}
                            onValueChange={value => {
                                this.setStateAddress("index", value);
                            }}
                        />
                    </div>
                </div>
                <div className={cn("row")}>
                    <div className={cn("label")}>Регион</div>
                    <div className={cn("field")}>
                        <ComboBox<ItemResult>
                            value={{ place: this.state.address.region }}
                            error={invalidField === "region"}
                            placeholder="Можно вводить код или название"
                            width="100%"
                            drawArrow={false}
                            {...this._regionProps}
                        />
                    </div>
                </div>
                <div className={cn("row")}>
                    <div className={cn("label")}>Район</div>
                    <div className={cn("field")}>
                        <ComboBox<ItemResult>
                            value={{ place: this.state.address.district }}
                            error={invalidField === "district"}
                            width="100%"
                            drawArrow={false}
                            {...this._districtProps}
                        />
                    </div>
                </div>
                <div className={cn("row")}>
                    <div className={cn("label")}>Город</div>
                    <div className={cn("field")}>
                        <ComboBox<ItemResult>
                            value={{ place: this.state.address.city }}
                            error={invalidField === "city"}
                            width="100%"
                            drawArrow={false}
                            {...this._cityProps}
                        />
                    </div>
                </div>
                <div className={cn("row")}>
                    <div className={cn("label")}>Населенный пункт</div>
                    <div className={cn("field")}>
                        <ComboBox<ItemResult>
                            value={{ place: this.state.address.settlement }}
                            error={invalidField === "settlement"}
                            width="100%"
                            drawArrow={false}
                            {...this._settlementProps}
                        />
                    </div>
                </div>
                <div className={cn("row")}>
                    <div className={cn("label")}>Улица</div>
                    <div className={cn("field")}>
                        <ComboBox<ItemResult>
                            value={{ place: this.state.address.street }}
                            error={invalidField === "street"}
                            width="100%"
                            drawArrow={false}
                            {...this._streetProps}
                        />
                    </div>
                </div>
                <div className={cn("row")}>
                    <div className={cn("label")}>Дом</div>
                    <div className={cn("field")}>
                        <Input
                            value={this.state.address.house ?? ""}
                            width={100}
                            data-tid={"KladrSimpleInput"}
                            {...this._houseProps}
                        />
                    </div>
                </div>
                <div className={cn("row")}>
                    <div className={cn("label")}>Корпус</div>
                    <div className={cn("field")}>
                        <Input
                            value={this.state.address.building || ""}
                            width={100}
                            data-tid={"KladrSimpleInput"}
                            {...this._buildingProps}
                        />
                    </div>
                </div>

                <div className={cn("row")}>
                    <div className={cn("label")}>Квартира / офис</div>
                    <div className={cn("field")}>
                        <Input
                            value={this.state.address.room ?? ""}
                            width="100px"
                            data-tid={"KladrSimpleInput"}
                            {...this._roomProps}
                        />
                    </div>
                </div>
            </Gapped>
        );
    }

    private renderItem(field: Place, parents: Place[], value: ItemResult): JSX.Element {
        const parentNames = [];
        for (let i = parents.length - 1; i >= 0; --i) {
            const parentField = parents[i];
            if (parentField == undefined) {
                break;
            }
            if (this.state.address[parentField]) {
                break;
            }

            const parent = value.info?.address[parentField];
            if (isPlaceDescription(parent)) {
                let parentName = parent.name;
                if (parentField === "region") {
                    parentName = parent.code.substr(0, 2) + " " + parentName;
                }
                parentNames.unshift(parentName);
            }
        }
        return (
            <div>
                {isPlaceDescription(value.place) ? util.placeName(value.place, field) : value.place}
                {parentNames.length > 0 && <div className={cn("menuItemParents")}>{parentNames.join(", ")}</div>}
            </div>
        );
    }

    private renderValue(type: Place, value: ItemResult): string {
        if (!value.place) {
            return "";
        }

        return isPlaceDescription(value.place) ? util.placeName(value.place, type) : "null";
    }
}
